/* eslint-disable react/jsx-props-no-spreading */ // react-dropzone
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Modal from 'react-modal';
import { useDropzone } from 'react-dropzone';
import cx from 'classnames';

import { FEATURES, canAccessFeature } from 'helpers/features';
import { ActionLink } from 'components/shared';
import useCompanyBillingStatus from 'components/billing/useCompanyBillingStatus';
import { LD_DOCUMENT_SIGNING } from 'constants/featureFlags';
import { DOCUMENT_MAX_SIZE_BYTES } from 'constants/files';
import useUploadCompanyDocuments from './useUploadCompanyDocuments';

const messages = defineMessages({
  addDocuments: {
    id: 'CompanyDocuments.upload.addDocuments',
    defaultMessage: 'Add Documents',
    description: 'Upload document header',
  },
  maxFileSize: {
    id: 'CompanyDocuments.upload.maxFileSize',
    defaultMessage: 'Maximum file size of 100MB.',
    description: 'Max File size message',
  },
  maxFileSizeSignature: {
    id: 'CompanyDocuments.upload.maxFileSizeSignatures',
    description: 'DropboxSign file size restrictions',
    defaultMessage:
      "If you'd like to get this document signed, upload as a .pdf or .docx file under 40MB and less than 500 pages.",
  },
  dragAndDropMessage: {
    id: 'CompanyDocuments.upload.dragAndDropMessage',
    defaultMessage:
      'Drag & Drop document here or click to upload to this project.',
    description:
      'message shown to the user showing where the drag and drop enabled upload area is',
  },
  uploadMessaging: {
    id: 'CompanyDocuments.upload.uploadMessaging',
    defaultMessage: 'Uploading {count} documents…',
    description: 'message shown to the user while when documents are uploading',
  },
});

const UploadCompanyDocumentModal = ({ close }) => {
  const { formatMessage } = useIntl();

  const flags = useFlags();

  const { loading, uploadProgress, uploadCompanyDocuments } =
    useUploadCompanyDocuments({
      onComplete: close,
    });

  const { featureSet } = useCompanyBillingStatus();
  const [filesLength, setFilesLength] = useState(0);

  const onDrop = async (_acceptedFiles, _rejectedFiles) => {
    setFilesLength(_acceptedFiles.length);
    await uploadCompanyDocuments({
      files: _acceptedFiles,
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'application/vnd.apple.numbers': ['.numbers'],
      'application/vnd.apple.pages': ['.pages'],
      'application/vnd.autodesk.autocad_dwg': ['.dwg'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
      'video/mp4': ['.mp4'],
      'video/x-ms-wmv': ['.wmv'],
      'video/quicktime': ['.mov'],
      'video/webm': ['.webm'],
    },
    maxSize: DOCUMENT_MAX_SIZE_BYTES,
    onDrop,
  });

  const hasSignatures = canAccessFeature(featureSet, FEATURES.DOCUMENT_SIGNING);

  const canRequestSignatures = flags[LD_DOCUMENT_SIGNING] && hasSignatures;

  const renderDropzoneBody = () => {
    if (loading) {
      return (
        <>
          <div className="upload-progress-bar">
            <span
              style={{
                width: `${uploadProgress}%`,
              }}
            />
          </div>
          <p className="upload-status">
            {formatMessage(messages.uploadMessaging, {
              count: filesLength,
            })}
          </p>
        </>
      );
    }

    return formatMessage(messages.dragAndDropMessage);
  };
  const photoDropZoneClasses = cx('photo-drop-zone', {
    'photo-drop-zone-active': isDragActive,
  });

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      onRequestClose={close}
      overlayClassName="modal"
      className="modal-content-wrap"
    >
      <ActionLink className="modal-close-button" onClick={close}>
        <i className="mdi mdi-close" />
      </ActionLink>
      <form>
        <h3 className="modal-title">{formatMessage(messages.addDocuments)}</h3>
        <span className="instructions">
          {formatMessage(messages.maxFileSize)}
          {canRequestSignatures ? (
            <> {formatMessage(messages.maxFileSizeSignature)}</>
          ) : null}
        </span>
        <div
          {...getRootProps()}
          className={photoDropZoneClasses}
          name="document-drop-zone"
        >
          <input {...getInputProps()} />
          <div>{renderDropzoneBody()}</div>
        </div>
      </form>
    </Modal>
  );
};

UploadCompanyDocumentModal.propTypes = {
  close: PropTypes.func.isRequired,
};

export default UploadCompanyDocumentModal;
